import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { trackTrialRequest } from "@/utils/googleAnalytics";
import { 
  GraduationCap, 
  Users, 
  Award, 
  Calendar, 
  BarChart3, 
  FileText,
  Target,
  Clock,
  Shield,
  Zap,
  BookOpen,
  CheckCircle,
  ExternalLink,
  Key,
  Video,
  Send,
  Star
} from "lucide-react";
import { PublicPasswordReset } from "@/components/auth/PublicPasswordReset";
import { DistrictNeedsAssessment } from "@/components/landing/DistrictNeedsAssessment";
import { PublicPOUpload } from "@/components/landing/PublicPOUpload";
import { z } from "zod";
import edugogyLogo from "@/assets/edugogy-logo.png";

const trialFormSchema = z.object({
  firstName: z.string().trim().min(1, "First name is required").max(50, "First name must be less than 50 characters"),
  lastName: z.string().trim().min(1, "Last name is required").max(50, "Last name must be less than 50 characters"),
  email: z.string().trim().email("Invalid email address").max(255, "Email must be less than 255 characters"),
  district: z.string().trim().min(1, "District is required").max(100, "District name must be less than 100 characters"),
  role: z.string().min(1, "Role is required"),
  state: z.string().min(1, "State is required"),
  message: z.string().max(1000, "Message must be less than 1000 characters"),
  demoScheduled: z.boolean().refine(val => val === true, "You must schedule a demo to proceed")
})

const DEMO_CALENDAR_URL = "https://calendar.app.google/KomoKDTgSAVyqw1x5"

const US_STATES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut", "Delaware",
  "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky",
  "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota", "Mississippi",
  "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire", "New Jersey", "New Mexico",
  "New York", "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon", "Pennsylvania",
  "Rhode Island", "South Carolina", "South Dakota", "Tennessee", "Texas", "Utah", "Vermont",
  "Virginia", "Washington", "West Virginia", "Wisconsin", "Wyoming"
];

const ROLES = [
  "Teacher", "Principal", "Administrator", "Superintendent", "Curriculum Coordinator",
  "Instructional Coach", "Department Head", "Assistant Principal", "IT Director", "Other"
];

interface TrialFormData {
  firstName: string;
  lastName: string;
  email: string;
  district: string;
  role: string;
  state: string;
  message: string;
  demoScheduled: boolean;
}

export default function Landing() {
  const [formData, setFormData] = useState<TrialFormData>({
    firstName: "",
    lastName: "",
    email: "",
    district: "",
    role: "",
    state: "",
    message: "",
    demoScheduled: false
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [activeTab, setActiveTab] = useState("assessment");

  const handleInputChange = (field: keyof TrialFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field as string]) {
      setErrors(prev => ({ ...prev, [field as string]: '' }))
    }
  };

  const handleTrialSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    try {
      // Validate form data
      const validatedData = trialFormSchema.parse(formData);
      
      const { error } = await supabase
        .from('trial_requests')
        .insert({
          first_name: validatedData.firstName,
          last_name: validatedData.lastName,
          email: validatedData.email,
          district: validatedData.district,
          role: validatedData.role,
          state: validatedData.state,
          message: validatedData.message
        });

      if (error) throw error;

      toast.success("Trial request submitted successfully! We'll be in touch soon.");
      
      // Track trial request in Google Analytics
      trackTrialRequest(validatedData.role, validatedData.state);
      
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        district: "",
        role: "",
        state: "",
        message: "",
        demoScheduled: false
      });
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: Record<string, string> = {}
        error.errors.forEach((err) => {
          if (err.path[0]) {
            fieldErrors[err.path[0] as string] = err.message
          }
        })
        setErrors(fieldErrors)
        toast.error('Please fix the errors below')
      } else {
        console.error('Error submitting trial request:', error);
        toast.error("Failed to submit trial request. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="light min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5">
      {/* Hero Section */}
      <section className="relative py-2 px-4">
        <div className="container mx-auto text-center">
          <div>
            <img 
              src={edugogyLogo} 
              alt="Edugogy" 
              fetchPriority="high"
              className="mx-auto mb-4 max-w-[200px] sm:max-w-[300px] md:max-w-[400px] lg:max-w-[500px] h-auto object-contain"
            />
          </div>
          <h1 className="text-5xl md:text-7xl font-bold bg-gradient-to-r from-primary via-primary/80 to-secondary bg-clip-text text-transparent mb-6">
            Professional Learning Made Simple
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-3">
            Streamline your district's professional development with our comprehensive platform designed for educators, by educators.
          </p>
          <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 justify-center items-center mb-6 max-w-4xl mx-auto">
            <Button 
              size="lg" 
              className="text-base px-6 py-4 w-full sm:w-auto"
              onClick={() => {
                setActiveTab("trial");
                document.getElementById('auth-section')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              <GraduationCap className="mr-2 h-4 w-4" />
              Start Your Trial
            </Button>
            <Button 
              variant="outline" 
              size="lg" 
              className="text-base px-6 py-4 w-full sm:w-auto"
              onClick={() => {
                setActiveTab("signin");
                document.getElementById('auth-section')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              <Users className="mr-2 h-4 w-4" />
              Sign In
            </Button>
            <Button 
              variant="secondary" 
              size="lg" 
              className="text-base px-6 py-4 w-full sm:w-auto"
              onClick={() => {
                setActiveTab("reset");
                document.getElementById('auth-section')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              <Key className="mr-2 h-4 w-4" />
              Reset Password
            </Button>
            <Button 
              variant="default" 
              size="lg" 
              className="text-base px-6 py-4 w-full sm:w-auto"
              onClick={() => {
                setActiveTab("assessment");
                document.getElementById('auth-section')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              <Target className="mr-2 h-4 w-4" />
              Try Needs Assessment
            </Button>
            <Button 
              variant="secondary" 
              size="lg" 
              className="text-base px-6 py-4 w-full sm:w-auto"
              onClick={() => {
                setActiveTab("upload-po");
                document.getElementById('auth-section')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              <FileText className="mr-2 h-4 w-4" />
              Upload Purchase Order
            </Button>
            <Button 
              variant="ghost" 
              size="lg" 
              className="text-base px-6 py-4 w-full sm:w-auto"
              onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
            >
              <BookOpen className="mr-2 h-4 w-4" />
              View Features
            </Button>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="py-20 px-4 bg-card/50">
        <div className="container mx-auto">
          <h2 className="text-4xl font-bold text-center mb-4">Everything You Need</h2>
          <p className="text-xl text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
            Comprehensive tools to manage, track, and enhance professional development across your district.
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader>
                <Users className="h-12 w-12 text-primary mb-4" />
                <CardTitle>User Management</CardTitle>
                <CardDescription>
                  Effortlessly manage staff, create user groups, and handle bulk enrollments with our intuitive admin tools.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader>
                <GraduationCap className="h-12 w-12 text-primary mb-4" />
                <CardTitle>Course Management</CardTitle>
                <CardDescription>
                  Create, organize, and deliver professional development courses with rich content and flexible delivery options.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader>
                <Target className="h-12 w-12 text-primary mb-4" />
                <CardTitle>AI Walkthrough Assistant</CardTitle>
                <CardDescription>
                  Conduct classroom observations with AI-powered analysis. Upload photos/videos for instant coaching recommendations or review teachers' practice videos.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader>
                <Zap className="h-12 w-12 text-primary mb-4" />
                <CardTitle>Smart Course Assignment</CardTitle>
                <CardDescription>
                  AI analyzes classroom walkthroughs and automatically recommends targeted professional development courses for each teacher.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader>
                <BookOpen className="h-12 w-12 text-primary mb-4" />
                <CardTitle>Practice Video Library</CardTitle>
                <CardDescription>
                  Teachers upload practice videos for self-reflection and coaching. Administrators review and provide personalized growth recommendations.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader>
                <Video className="h-12 w-12 text-primary mb-4" />
                <CardTitle>Video Library</CardTitle>
                <CardDescription>
                  Centralized video content library for professional development. Share instructional videos, best practices, and training materials across your district.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader>
                <Send className="h-12 w-12 text-primary mb-4" />
                <CardTitle>Video Assignments</CardTitle>
                <CardDescription>
                  Assign specific videos to teachers for targeted professional learning. Track completion and gather feedback on video-based training.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader>
                <Star className="h-12 w-12 text-primary mb-4" />
                <CardTitle>NBCT Coaching</CardTitle>
                <CardDescription>
                  Support teachers pursuing National Board Certification with guided coaching, portfolio building tools, and alignment to NBCT Core Propositions.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader>
                <Award className="h-12 w-12 text-primary mb-4" />
                <CardTitle>Badges & Certificates</CardTitle>
                <CardDescription>
                  Recognize achievements with customizable digital badges and professional certificates.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader>
                <Calendar className="h-12 w-12 text-primary mb-4" />
                <CardTitle>Meeting Management</CardTitle>
                <CardDescription>
                  Schedule observations, track mentoring meetings, and maintain comprehensive records.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader>
                <BarChart3 className="h-12 w-12 text-primary mb-4" />
                <CardTitle>Analytics & Reporting</CardTitle>
                <CardDescription>
                  Generate detailed reports and gain insights into professional development effectiveness.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader>
                <FileText className="h-12 w-12 text-primary mb-4" />
                <CardTitle>Survey & Evaluation</CardTitle>
                <CardDescription>
                  Collect feedback and evaluate course effectiveness with customizable surveys and templates.
                </CardDescription>
              </CardHeader>
            </Card>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-20 px-4">
        <div className="container mx-auto">
          <h2 className="text-4xl font-bold text-center mb-12">Why Choose Edugogy?</h2>
          
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <Target className="h-8 w-8 text-primary mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-semibold mb-2">Targeted Learning</h3>
                  <p className="text-muted-foreground">
                    Deliver personalized professional development based on roles, departments, and individual needs.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <Clock className="h-8 w-8 text-primary mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-semibold mb-2">Time-Saving Automation</h3>
                  <p className="text-muted-foreground">
                    Automate enrollment, notifications, and tracking to focus on what matters most - teaching.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <Shield className="h-8 w-8 text-primary mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-semibold mb-2">Secure & Compliant</h3>
                  <p className="text-muted-foreground">
                    Built with security and educational compliance in mind, protecting your district's data.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <Zap className="h-8 w-8 text-primary mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-semibold mb-2">Easy Integration</h3>
                  <p className="text-muted-foreground">
                    Seamlessly integrate with existing systems including SIS platforms and single sign-on.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-primary/10 to-secondary/10 p-8 rounded-2xl">
              <h3 className="text-2xl font-bold mb-6">Ready to Transform Your PD?</h3>
              <div className="space-y-4 mb-6">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-primary" />
                  <span>30-day free trial</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-primary" />
                  <span>No setup fees</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-primary" />
                  <span>Full feature access</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-primary" />
                  <span>Dedicated support</span>
                </div>
              </div>
            <Button 
              size="lg" 
              className="w-full"
              onClick={() => window.location.href = '/auth'}
            >
              Get Started Today
            </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Authentication Section */}
      <section id="auth-section" className="py-20 px-4 bg-card/50">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-4xl font-bold text-center mb-12">Get Started</h2>
          
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full" id="main-tabs">
            <TabsList className="grid w-full grid-cols-2 md:grid-cols-5 mb-24 md:mb-8 gap-2 sticky top-0 z-50 bg-background p-2 shadow-sm">
              <TabsTrigger value="assessment" className="text-xs md:text-sm py-2.5 px-2">Needs Assessment</TabsTrigger>
              <TabsTrigger value="upload-po" className="text-xs md:text-sm py-2.5 px-2">Upload PO</TabsTrigger>
              <TabsTrigger value="trial" className="text-xs md:text-sm py-2.5 px-2">Request Trial</TabsTrigger>
              <TabsTrigger value="signin" className="text-xs md:text-sm py-2.5 px-2">Sign In</TabsTrigger>
              <TabsTrigger value="reset" className="text-xs md:text-sm py-2.5 px-2 col-span-2 md:col-span-1">Reset Password</TabsTrigger>
            </TabsList>

            <TabsContent value="assessment" className="relative z-0">
              <DistrictNeedsAssessment />
            </TabsContent>

            <TabsContent value="upload-po" className="relative z-0">
              <PublicPOUpload />
            </TabsContent>

            <TabsContent value="trial" className="relative z-0">
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-2xl">Start Your Free Trial</CardTitle>
                  <CardDescription>
                    Experience the full power of Edugogy with a 30-day free trial. <strong>A demo call is required</strong> before we can activate your trial.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Alert className="mb-6 border-blue-200 bg-blue-50">
                    <Calendar className="h-4 w-4" />
                    <AlertDescription>
                      <strong>Step 1:</strong> Schedule your required demo call first, then complete this form.
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="mt-2 w-full"
                        onClick={() => window.open(DEMO_CALENDAR_URL, '_blank')}
                      >
                        <Calendar className="h-4 w-4 mr-2" />
                        Schedule Demo Call
                        <ExternalLink className="h-4 w-4 ml-2" />
                      </Button>
                    </AlertDescription>
                  </Alert>
                  
                  <form onSubmit={handleTrialSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="firstName">First Name</Label>
                        <Input
                          id="firstName"
                          value={formData.firstName}
                          onChange={(e) => handleInputChange('firstName', e.target.value)}
                          required
                          className={errors.firstName ? "border-red-500" : ""}
                        />
                        {errors.firstName && <p className="text-sm text-red-500">{errors.firstName}</p>}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="lastName">Last Name</Label>
                        <Input
                          id="lastName"
                          value={formData.lastName}
                          onChange={(e) => handleInputChange('lastName', e.target.value)}
                          required
                          className={errors.lastName ? "border-red-500" : ""}
                        />
                        {errors.lastName && <p className="text-sm text-red-500">{errors.lastName}</p>}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        required
                        className={errors.email ? "border-red-500" : ""}
                      />
                      {errors.email && <p className="text-sm text-red-500">{errors.email}</p>}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="district">School District</Label>
                      <Input
                        id="district"
                        value={formData.district}
                        onChange={(e) => handleInputChange('district', e.target.value)}
                        required
                        className={errors.district ? "border-red-500" : ""}
                      />
                      {errors.district && <p className="text-sm text-red-500">{errors.district}</p>}
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="role">Role</Label>
                        <Select onValueChange={(value) => handleInputChange('role', value)} required>
                          <SelectTrigger className={errors.role ? "border-red-500" : ""}>
                            <SelectValue placeholder="Select your role" />
                          </SelectTrigger>
                          <SelectContent>
                            {ROLES.map((role) => (
                              <SelectItem key={role} value={role}>
                                {role}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {errors.role && <p className="text-sm text-red-500">{errors.role}</p>}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="state">State</Label>
                        <Select onValueChange={(value) => handleInputChange('state', value)} required>
                          <SelectTrigger className={errors.state ? "border-red-500" : ""}>
                            <SelectValue placeholder="Select your state" />
                          </SelectTrigger>
                          <SelectContent>
                            {US_STATES.map((state) => (
                              <SelectItem key={state} value={state}>
                                {state}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {errors.state && <p className="text-sm text-red-500">{errors.state}</p>}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message">Additional Information (Optional)</Label>
                      <Textarea
                        id="message"
                        placeholder="Tell us about your district's professional development needs..."
                        value={formData.message}
                        onChange={(e) => handleInputChange('message', e.target.value)}
                        className={errors.message ? "border-red-500" : ""}
                      />
                      {errors.message && <p className="text-sm text-red-500">{errors.message}</p>}
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="demoScheduled"
                          checked={formData.demoScheduled}
                          onCheckedChange={(checked) => handleInputChange('demoScheduled', !!checked)}
                        />
                        <Label 
                          htmlFor="demoScheduled" 
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          I have scheduled a demo call using the link above
                        </Label>
                      </div>
                      {errors.demoScheduled && <p className="text-sm text-red-500">{errors.demoScheduled}</p>}
                    </div>

                    <Button type="submit" size="lg" className="w-full" disabled={loading || !formData.demoScheduled}>
                      {loading ? "Submitting..." : "Request Free Trial"}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="signin" className="relative z-0">
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-2xl">Already Have an Account?</CardTitle>
                  <CardDescription>
                    Sign in to access your Edugogy dashboard and continue your professional development journey.
                  </CardDescription>
                </CardHeader>
                <CardContent className="text-center">
                  <Button 
                    size="lg" 
                    className="w-full mb-4"
                    onClick={() => window.location.href = '/auth'}
                  >
                    Go to Sign In
                  </Button>
                  <p className="text-sm text-muted-foreground">
                    Forgot your password? Use the "Reset Password" tab above.
                  </p>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="reset" className="relative z-0">
              <PublicPasswordReset />
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 bg-card border-t">
        <div className="container mx-auto text-center">
          <img 
            src={edugogyLogo} 
            alt="Edugogy" 
            className="mx-auto h-6 sm:h-8 w-auto mb-4 opacity-70"
          />
          <p className="text-muted-foreground">
            © 2024 Edugogy. Empowering educators through innovative professional development.
          </p>
        </div>
      </footer>
    </div>
  );
}